import { useState, useEffect } from 'react'
import Api from '@vacationtracker/shared/services/api'
import { RechargeType, ChallengeInfo } from '@vacationtracker/shared/types/recharge-type'
import { UserRole } from '@vacationtracker/shared/types/user'

interface UseEnergyScoreProps {
  currentUserId?: string
  userRole?: string
  energyLevelAccess?: UserRole[]
}

export interface RechargeDetails {
  rechargeType: RechargeType
  completedAt: string
  challengeInfo: ChallengeInfo
}

interface UseEnergyScoreReturn {
  energyScore: number
  isLoading: boolean
  error: string | null
  isFeatureEnabled: boolean
  shouldShowEnergyScore: boolean
  rechargeDetails: RechargeDetails | null
  refetch: () => void
}

/**
 * Custom hook for managing energy score functionality
 * Simple implementation without Redux - perfect for single-component use
 *
 * @param currentUserId - Current authenticated user ID
 * @param userRole - Current user's role
 * @param energyLevelAccess - Roles allowed to see energy level (from featurePermissions)
 */
export const useEnergyScore = ({ currentUserId, userRole, energyLevelAccess }: UseEnergyScoreProps): UseEnergyScoreReturn => {
  const [energyScore, setEnergyScore] = useState<number>(0)
  const [rechargeDetails, setRechargeDetails] = useState<RechargeDetails | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isForbidden, setIsForbidden] = useState(false)

  // Feature access is determined by featurePermissions.energyLevelAccess
  // When energyLevelAccess is null/undefined, the feature is enabled for all roles
  const isFeatureEnabled = !energyLevelAccess || energyLevelAccess.includes(userRole as UserRole)

  // Determine if we should show the energy score
  const shouldShowEnergyScore = isFeatureEnabled && !isLoading && !error && energyScore > 0 && !isForbidden

  const fetchEnergyScore = async () => {
    if (!isFeatureEnabled || !currentUserId) return

    try {
      setIsLoading(true)
      setError(null)

      const response = await Api.get('/insights/me')

      setIsForbidden(false)
      if (response?.userInsight?.energyScore?.score) {
        setEnergyScore(response.userInsight.energyScore.score as number)
      } else {
        setEnergyScore(0)
      }

      // Extract rechargeDetails from response
      if (response?.rechargeDetails) {
        setRechargeDetails(response.rechargeDetails as RechargeDetails)
      } else {
        setRechargeDetails(null)
      }
    } catch (err) {
      if (err && typeof err === 'object' && (err as Record<string, unknown>).message === 'AccessForbidden') {
        setIsForbidden(true)
      } else {
        setError('Failed to fetch energy score')
      }
      setEnergyScore(0)
    } finally {
      setIsLoading(false)
    }
  }

  // Fetch energy score when dependencies change
  useEffect(() => {
    if (isFeatureEnabled && currentUserId) {
      fetchEnergyScore()
    }
  }, [isFeatureEnabled, currentUserId, energyLevelAccess])

  return {
    energyScore,
    isLoading,
    error,
    isFeatureEnabled,
    shouldShowEnergyScore,
    rechargeDetails,
    refetch: fetchEnergyScore,
  }
}
